import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth, startGoogleSignIn } from "../lib/auth";
import { toast } from "sonner";
import { Plus, Edit2, Trash2, Loader2, LogOut, AlertCircle, ExternalLink } from "lucide-react";

const STATUS_BADGE = {
  pending: { text: "Pending review", cls: "bg-amber-200 text-zinc-900 border-amber-500" },
  pending_payment: { text: "Awaiting donation", cls: "bg-amber-200 text-zinc-900 border-amber-500" },
  active: { text: "Live", cls: "bg-green-500 text-white border-green-700" },
  paused: { text: "Paused — donate to reactivate", cls: "bg-zinc-300 text-zinc-900 border-zinc-500" },
  rejected: { text: "Rejected", cls: "bg-red-600 text-white border-red-700" },
};

export default function MyCampaigns() {
  const { user, loading, signOut } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null); // null | "new" | campaign id

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/campaigns/mine");
      setItems(data.items || []);
    } catch {
      /* not authed */
    }
  }, []);

  useEffect(() => {
    if (!loading && user) load();
  }, [loading, user, load]);

  if (loading) {
    return <div className="min-h-[40vh] flex items-center justify-center"><Loader2 className="animate-spin" /></div>;
  }

  if (!user) {
    return (
      <div className="max-w-md mx-auto px-6 py-20 text-center" data-testid="my-campaigns-signed-out">
        <h1 className="font-display font-black text-3xl">Sign in to manage your ads</h1>
        <p className="mt-3 text-zinc-600">Use the same Google account every time so your campaigns stay linked to you.</p>
        <button
          type="button"
          onClick={() => startGoogleSignIn("/my-campaigns")}
          className="mt-6 inline-flex items-center gap-2 bg-white border-2 border-zinc-900 text-zinc-900 px-6 py-3 font-bold uppercase tracking-widest text-sm hover:bg-zinc-900 hover:text-white transition-colors shadow-[4px_4px_0_0_#18181b]"
          data-testid="my-campaigns-signin-btn"
        >
          Sign in with Google
        </button>
        <div className="mt-6 text-xs">
          <Link to="/advertise" className="underline text-zinc-600">Read the advertiser instructions →</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-6 py-12" data-testid="my-campaigns-page">
      <div className="flex items-start justify-between gap-4 mb-8">
        <div>
          <div className="label-eyebrow mb-1">Advertiser dashboard</div>
          <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tight">
            Hi, {user.name?.split(" ")[0] || "friend"}
          </h1>
          <p className="text-zinc-600 text-sm mt-1">{user.email}</p>
        </div>
        <button
          type="button"
          onClick={async () => { await signOut(); navigate("/"); }}
          className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-zinc-600 hover:text-red-600"
          data-testid="my-campaigns-signout"
        >
          <LogOut size={13} /> Sign out
        </button>
      </div>

      <div className="border border-zinc-300 bg-amber-50 p-4 mb-8 flex items-start gap-3 text-sm">
        <AlertCircle size={16} className="text-amber-700 flex-shrink-0 mt-0.5" />
        <div>
          <strong>Sponsor donation is $100 per 30-day slot — not a subscription</strong>. After your
          campaign is approved, donate $100 via PayPal, Cash App, or Zelle (handles on the{" "}
          <Link to="/advertise" className="underline">Advertise page</Link>) and include your campaign ID in the note.
          Your ad goes live within 24 hours. We'll send a friendly reminder ~5 days before it expires so you can decide
          whether to donate again — there's no auto-charge.
        </div>
      </div>

      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display font-bold text-xl">Your campaigns</h2>
        {editing !== "new" && (
          <button
            type="button"
            onClick={() => setEditing("new")}
            className="inline-flex items-center gap-2 bg-zinc-900 text-white px-4 py-2 text-xs font-bold uppercase tracking-widest hover:bg-red-600 transition-colors"
            data-testid="my-campaigns-new-btn"
          >
            <Plus size={13} /> New campaign
          </button>
        )}
      </div>

      {editing === "new" && (
        <CampaignForm
          onCancel={() => setEditing(null)}
          onSaved={(c) => { setEditing(null); setItems((prev) => [c, ...prev]); }}
          busy={busy}
          setBusy={setBusy}
        />
      )}

      {items.length === 0 && editing !== "new" && (
        <div className="border border-dashed border-zinc-300 p-10 text-center text-sm text-zinc-500" data-testid="my-campaigns-empty">
          No campaigns yet. Click "New campaign" to submit your first ad.
        </div>
      )}

      <div className="space-y-4 mt-4">
        {items.map((c) => (
          <div key={c.id} className="border border-zinc-300 bg-white p-5" data-testid={`campaign-${c.id}`}>
            {editing === c.id ? (
              <CampaignForm
                initial={c}
                onCancel={() => setEditing(null)}
                onSaved={(updated) => {
                  setEditing(null);
                  setItems((prev) => prev.map((p) => (p.id === c.id ? updated : p)));
                }}
                busy={busy}
                setBusy={setBusy}
              />
            ) : (
              <CampaignRow c={c} onEdit={() => setEditing(c.id)} onDelete={async () => {
                if (!window.confirm("Delete this campaign?")) return;
                setBusy(true);
                try {
                  await api.delete(`/campaigns/${c.id}`);
                  setItems((prev) => prev.filter((p) => p.id !== c.id));
                  toast.success("Campaign deleted");
                } catch {
                  toast.error("Could not delete");
                } finally { setBusy(false); }
              }} />
            )}
          </div>
        ))}
      </div>

      <div className="mt-12 text-xs text-zinc-500 font-mono">
        Need help? <a href="mailto:info@pawsonrecord.com" className="underline">info@pawsonrecord.com</a>
        {" · "}
        <Link to="/advertise#troubleshooting" className="underline">Troubleshooting →</Link>
      </div>
    </div>
  );
}

function CampaignRow({ c, onEdit, onDelete }) {
  const badge = STATUS_BADGE[c.status] || { text: c.status, cls: "bg-zinc-100 text-zinc-900 border-zinc-300" };
  return (
    <div className="grid grid-cols-1 md:grid-cols-[140px_1fr_auto] gap-4">
      <div className="aspect-[4/3] bg-zinc-100 border border-zinc-200 overflow-hidden">
        <img src={c.ad_image_url} alt={c.ad_title} className="w-full h-full object-cover"
             onError={(e) => { e.currentTarget.style.display = "none"; }} />
      </div>
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest border ${badge.cls}`}>
            {badge.text}
          </span>
          <span className="font-mono text-[11px] text-zinc-500" data-testid={`campaign-id-${c.id}`}>
            ID: {c.id}
          </span>
        </div>
        <div className="font-display font-bold text-lg mt-1">{c.ad_title}</div>
        <div className="text-sm text-zinc-700">{c.advertiser_name}</div>
        <a href={c.ad_link_url} target="_blank" rel="noreferrer"
           className="text-xs text-red-600 hover:underline inline-flex items-center gap-1 mt-1">
          {c.ad_link_url} <ExternalLink size={11} />
        </a>
        {c.paid_through && (
          <div className="text-[11px] font-mono text-zinc-500 mt-2">
            Paid through: {new Date(c.paid_through).toLocaleDateString()}
          </div>
        )}
        {c.admin_notes && (
          <div className="text-[12px] mt-2 p-2 bg-amber-50 border border-amber-200">
            <strong>Admin note:</strong> {c.admin_notes}
          </div>
        )}
      </div>
      <div className="flex md:flex-col gap-2 md:items-end">
        <Link to={`/thanks/${c.id}`}
              className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-widest text-green-700 hover:text-green-900"
              data-testid={`campaign-thanks-${c.id}`}>
          <ExternalLink size={12} /> Thank-you page
        </Link>
        <button type="button" onClick={onEdit}
                className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-widest text-zinc-600 hover:text-zinc-900"
                data-testid={`campaign-edit-${c.id}`}>
          <Edit2 size={12} /> Edit
        </button>
        <button type="button" onClick={onDelete}
                className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-widest text-red-600 hover:text-red-800"
                data-testid={`campaign-delete-${c.id}`}>
          <Trash2 size={12} /> Delete
        </button>
      </div>
    </div>
  );
}

function CampaignForm({ initial, onCancel, onSaved, busy, setBusy }) {
  const [form, setForm] = useState({
    advertiser_name: initial?.advertiser_name || "",
    ad_title: initial?.ad_title || "",
    ad_image_url: initial?.ad_image_url || "",
    ad_link_url: initial?.ad_link_url || "",
    contact_email: initial?.contact_email || "",
    public_listing: initial?.public_listing !== undefined ? initial.public_listing : true,
  });
  const isEdit = !!initial;
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = isEdit
        ? await api.patch(`/campaigns/${initial.id}`, form)
        : await api.post("/campaigns", form);
      toast.success(isEdit ? "Campaign updated — back to pending review." : "Campaign submitted! Donate $100 to take it live.");
      onSaved(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save campaign");
    } finally { setBusy(false); }
  };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="campaign-form">
      <Field label="Business / advertiser name" required>
        <input type="text" required value={form.advertiser_name} onChange={set("advertiser_name")}
               placeholder="Acme Pet Supplies" data-testid="campaign-form-advertiser-name"
               className="w-full px-3 py-2 border border-zinc-300 focus:outline-none focus:border-zinc-900" />
      </Field>
      <Field label="Ad headline (≤80 chars)" required>
        <input type="text" required maxLength={80} value={form.ad_title} onChange={set("ad_title")}
               placeholder="20% off premium kibble — free shipping over $50"
               data-testid="campaign-form-ad-title"
               className="w-full px-3 py-2 border border-zinc-300 focus:outline-none focus:border-zinc-900" />
      </Field>
      <Field label="Ad image URL (public link, .jpg/.png/.webp, 4:3 ideal)" required>
        <input type="url" required value={form.ad_image_url} onChange={set("ad_image_url")}
               placeholder="https://example.com/myad.jpg"
               data-testid="campaign-form-image-url"
               className="w-full px-3 py-2 border border-zinc-300 focus:outline-none focus:border-zinc-900 font-mono text-xs" />
      </Field>
      <Field label="Click-through URL (where people go when they tap your ad)" required>
        <input type="url" required value={form.ad_link_url} onChange={set("ad_link_url")}
               placeholder="https://yourbusiness.com"
               data-testid="campaign-form-link-url"
               className="w-full px-3 py-2 border border-zinc-300 focus:outline-none focus:border-zinc-900 font-mono text-xs" />
      </Field>
      <Field label="Contact email (for donation receipts & approval notices)" required>
        <input type="email" required value={form.contact_email} onChange={set("contact_email")}
               placeholder="you@yourbusiness.com"
               data-testid="campaign-form-contact-email"
               className="w-full px-3 py-2 border border-zinc-300 focus:outline-none focus:border-zinc-900" />
      </Field>
      <label className="flex items-start gap-3 p-3 border border-zinc-200 bg-zinc-50 cursor-pointer hover:border-zinc-400">
        <input
          type="checkbox"
          checked={form.public_listing}
          onChange={(e) => setForm((f) => ({ ...f, public_listing: e.target.checked }))}
          className="mt-1 flex-shrink-0"
          data-testid="campaign-form-public-listing"
        />
        <span className="text-sm text-zinc-700 leading-relaxed">
          <strong>List my business on the public Donors Wall.</strong> Adds your business name to{" "}
          <a href="/sponsors" target="_blank" rel="noreferrer" className="underline">/sponsors</a>{" "}
          and the homepage sponsor strip — even after your slot expires. Uncheck if you'd rather
          stay anonymous.
        </span>
      </label>
      <div className="flex gap-3 pt-2">
        <button type="submit" disabled={busy}
                className="inline-flex items-center gap-2 bg-zinc-900 text-white px-5 py-2 text-xs font-bold uppercase tracking-widest hover:bg-red-600 disabled:opacity-50 transition-colors"
                data-testid="campaign-form-submit">
          {busy && <Loader2 size={13} className="animate-spin" />}
          {isEdit ? "Save changes" : "Submit for review"}
        </button>
        <button type="button" onClick={onCancel} disabled={busy}
                className="text-xs font-bold uppercase tracking-widest text-zinc-600 hover:text-zinc-900 disabled:opacity-50"
                data-testid="campaign-form-cancel">
          Cancel
        </button>
      </div>
    </form>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <div className="text-[11px] uppercase tracking-widest font-bold text-zinc-700 mb-1">
        {label} {required && <span className="text-red-600">*</span>}
      </div>
      {children}
    </label>
  );
}
